import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { RelationDto } from '@nw/contracts';
import type { GraphSnapshot } from '@nw/graph-contracts';
import { ApiError, api } from '@nw/web-platform';
import type { Concept, MindNote, SavedView, VideoSource } from '../public/index.js';

export type GraphParams = {
  root?: string;
  depth?: number;
  modules?: string[];
  types?: string[];
  predicates?: string[];
  limit?: number;
};
export type GraphResponse = {
  snapshot: GraphSnapshot;
  relations: RelationDto[];
};

export function graphQueryString(p: GraphParams) {
  const s = new URLSearchParams();
  if (p.root) s.set('root', p.root);
  if (p.depth) s.set('depth', String(p.depth));
  if (p.modules?.length) s.set('modules', p.modules.join(','));
  if (p.types?.length) s.set('types', p.types.join(','));
  if (p.predicates?.length) s.set('predicates', p.predicates.join(','));
  if (p.limit) s.set('limit', String(p.limit));
  return s.toString();
}

export function useGraph(p: GraphParams) {
  const qs = graphQueryString(p);
  return useQuery({
    queryKey: ['graph', qs],
    queryFn: async ({ signal }) => (await api<GraphResponse>(`/graph?${qs}`, { signal })).data,
    placeholderData: keepPreviousData,
    // A root that was just created may not be projected yet: retry briefly before reporting it missing.
    retry: (count, err) => !!p.root && err instanceof ApiError && err.status === 404 && count < 5,
    retryDelay: 1000,
  });
}

export function useViews() {
  return useQuery({
    queryKey: ['mind', 'views'],
    queryFn: async () => (await api<SavedView[]>('/mind/views')).data,
  });
}

function useRefreshNetwork() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['graph'] });
    void qc.invalidateQueries({ queryKey: ['entity'] });
    void qc.invalidateQueries({ queryKey: ['mind'] });
    void qc.invalidateQueries({ queryKey: ['home'] });
    void qc.invalidateQueries({ queryKey: ['pending-projections'] });
  };
}

export function useMindMutations() {
  const refresh = useRefreshNetwork();
  return {
    createConcept: useMutation({
      mutationFn: async (v: { title: string; body?: string }) => (await api<Concept>('/mind/concepts', { body: v })).data,
      onSuccess: refresh,
    }),
    updateConcept: useMutation({
      mutationFn: async ({ id, ...v }: { id: string; title?: string; body?: string; expectedRevision: number }) =>
        (
          await api<Concept>(`/mind/concepts/${id}`, {
            method: 'PATCH',
            body: v,
          })
        ).data,
      onSuccess: refresh,
    }),
    createNote: useMutation({
      mutationFn: async (v: { title?: string; body: string }) => (await api<MindNote>('/mind/notes', { body: v })).data,
      onSuccess: refresh,
    }),
    updateNote: useMutation({
      mutationFn: async ({ id, ...v }: { id: string; title?: string; body?: string; expectedRevision: number }) => (await api<MindNote>(`/mind/notes/${id}`, { method: 'PATCH', body: v })).data,
      onSuccess: refresh,
    }),
    createSource: useMutation({
      mutationFn: async (v: { url: string; title?: string; userNote?: string }) =>
        api<VideoSource, { lookupError: string | null }>('/mind/sources', {
          body: v,
        }),
      onSuccess: refresh,
    }),
    updateSource: useMutation({
      mutationFn: async ({ id, ...v }: { id: string; title?: string; userNote?: string; expectedRevision: number }) =>
        (
          await api<VideoSource>(`/mind/sources/${id}`, {
            method: 'PATCH',
            body: v,
          })
        ).data,
      onSuccess: refresh,
    }),
    refreshSource: useMutation({
      mutationFn: async (id: string) =>
        (
          await api<VideoSource>(`/mind/sources/${id}/refresh`, {
            method: 'POST',
          })
        ).data,
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: async (v: { kind: 'concepts' | 'notes' | 'sources'; id: string; expectedRevision: number }) =>
        api(`/mind/${v.kind}/${v.id}`, {
          method: 'DELETE',
          body: { expectedRevision: v.expectedRevision },
        }),
      onSuccess: refresh,
    }),
    connect: useMutation({
      mutationFn: async (v: { from: string; to: string; predicate: string; explanation: string }) => (await api<RelationDto>('/relations', { body: v })).data,
      onSuccess: refresh,
    }),
    disconnect: useMutation({
      mutationFn: async (id: string) => api(`/relations/${id}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
    explain: useMutation({
      mutationFn: async (v: { id: string; explanation: string; expectedRevision: number }) =>
        (
          await api<RelationDto>(`/relations/${v.id}`, {
            method: 'PATCH',
            body: {
              explanation: v.explanation,
              expectedRevision: v.expectedRevision,
            },
          })
        ).data,
      onSuccess: refresh,
    }),
  };
}

export function useViewMutations() {
  const qc = useQueryClient();
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['mind', 'views'] });
    void qc.invalidateQueries({ queryKey: ['home'] });
  };
  return {
    create: useMutation({
      mutationFn: async (v: Omit<SavedView, 'id' | 'createdAt' | 'updatedAt' | 'revision'>) => (await api<SavedView>('/mind/views', { body: v })).data,
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: async ({ id, ...v }: Partial<SavedView> & { id: string; expectedRevision: number }) =>
        (
          await api<SavedView>(`/mind/views/${id}`, {
            method: 'PATCH',
            body: v,
          })
        ).data,
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => api(`/mind/views/${id}`, { method: 'DELETE' }),
      onSuccess: done,
    }),
  };
}

export function useNativeRecord(key: string | null) {
  const [, type, id] = key?.split(':') ?? [];
  const path = key?.startsWith('mind-networks:') ? (type === 'concept' ? `/mind/concepts/${id}` : type === 'note' ? `/mind/notes/${id}` : type === 'video' ? `/mind/sources/${id}` : null) : null;
  return useQuery({
    queryKey: ['mind', 'record', key],
    queryFn: async () => (await api<Concept | MindNote | VideoSource>(path!)).data,
    enabled: !!path,
    retry: false,
  });
}
